<template>
  <!--
    AB#22138 — the Notifications sheet: notifications on this device, and which
    kinds reach the person. Every kind starts on; the switches apply to every
    device the person turned notifications on. Only the kinds that can reach
    this person are listed (the server decides, from the same permissions the
    senders check).
  -->
  <Drawer
    title="Notifications"
    subtitle="The phone's own Focus and preview settings still apply."
    width="440px"
    data-test="push-settings"
    @close="close"
  >
    <section class="ns-device" data-test="push-settings-device">
      <div class="ns-text">
        <b>On this device</b>
        <small v-if="push.state.value === 'needs-install'">
          On an iPhone, add the portal to your Home Screen first, and open it from there.
        </small>
        <small v-else-if="push.state.value === 'blocked'">
          Blocked in this phone's or browser's settings. Allow notifications for the portal there.
        </small>
        <small v-else>{{
          push.state.value === 'on' ? 'Notifications are on.' : 'Notifications are off.'
        }}</small>
      </div>
      <button
        v-if="push.state.value === 'on' || push.state.value === 'off'"
        type="button"
        class="ns-switch"
        :class="{ on: push.state.value === 'on' }"
        role="switch"
        :aria-checked="push.state.value === 'on'"
        :aria-busy="push.busy.value"
        aria-label="Notifications on this device"
        data-test="push-settings-device-switch"
        @click="toggleDevice"
      >
        <span class="ns-dot"></span>
      </button>
    </section>

    <h3 class="ns-head">What reaches you</h3>
    <ul class="ns-list" :class="{ 'ns-list--off': push.state.value !== 'on' }">
      <li v-for="p in rows" :key="p.type" class="ns-row" :data-test="`push-pref-${p.type}`">
        <div class="ns-text">
          <b>
            {{ LABELS[p.type].title }}
            <span v-if="LABELS[p.type].isNew" class="ns-new">New</span>
          </b>
          <small>{{ LABELS[p.type].text }}</small>
        </div>
        <button
          type="button"
          class="ns-switch"
          :class="{ on: p.enabled }"
          role="switch"
          :aria-checked="p.enabled"
          :aria-label="LABELS[p.type].title"
          @click="toggle(p.type, !p.enabled)"
        >
          <span class="ns-dot"></span>
        </button>
      </li>
    </ul>
    <p v-if="push.state.value !== 'on'" class="ns-note">
      These apply to every device you turn notifications on for.
    </p>
  </Drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Drawer from '@/components/Shared/Drawer.vue';
import { showToast } from '@/composables/swal';
import { usePushNotifications, type PushType } from '@/composables/usePushNotifications';

const push = usePushNotifications();

const LABELS: Record<PushType, { title: string; text: string; isNew?: boolean }> = {
  chat: { title: 'Mentions and replies', text: 'Team chat on your orders' },
  desk: { title: 'Desk messages', text: 'When a rule picks you' },
  assigned: {
    title: 'Orders assigned to me',
    text: 'As order admin or second order admin',
    isNew: true,
  },
  account_orders: {
    title: 'New orders on my accounts',
    text: 'Every new order on an account you look after',
    isNew: true,
  },
  delayed: {
    title: 'Delayed orders',
    text: 'When an order in your queue turns red. Ones from overnight arrive together at 7:00.',
    isNew: true,
  },
};

const rows = computed(() => push.preferences.value.filter((p) => p.type in LABELS));

const close = (): void => {
  push.settingsOpen.value = false;
};

const toggle = async (type: PushType, enabled: boolean): Promise<void> => {
  if (!(await push.setPreference(type, enabled))) {
    showToast('That could not be saved. Try again.', 'error');
  }
};

const toggleDevice = async (): Promise<void> => {
  if (push.busy.value) return;
  if (push.state.value === 'on') {
    await push.disable();
    return;
  }
  if (await push.enable()) {
    showToast('Notifications are on. A test is on its way.', 'success');
    void push.test();
  } else if ((push.state.value as string) === 'blocked') {
    showToast(
      "Notifications were not allowed. You can allow them in the phone's settings.",
      'warning'
    );
  }
};
</script>

<style scoped lang="scss">
.ns-device {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface-2);
}
.ns-head {
  margin: 20px 0 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
}
.ns-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ns-list--off {
  opacity: 0.6;
}
.ns-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-soft, var(--border));
}
.ns-row:last-child {
  border-bottom: 0;
}
.ns-text {
  min-width: 0;
}
.ns-text b {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}
.ns-text small {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--ink-mute);
}
.ns-new {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  vertical-align: 2px;
  color: var(--accent);
  background: var(--accent-soft);
}
.ns-note {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--ink-mute);
}
.ns-switch {
  flex: none;
  position: relative;
  width: 44px;
  height: 26px;
  border: 0;
  border-radius: 999px;
  background: var(--surface-3);
  cursor: pointer;
  transition: background 0.15s ease;
}
.ns-switch.on {
  background: var(--accent);
}
.ns-switch:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.ns-dot {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease;
}
.ns-switch.on .ns-dot {
  transform: translateX(18px);
}
@media (prefers-reduced-motion: reduce) {
  .ns-switch,
  .ns-dot {
    transition: none;
  }
}
</style>
